import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Service pour gérer les notifications métier utilisateur (solde, achats, transactions, etc.)
export class NotificationService {
  
  // Types de notifications métier
  static NOTIFICATION_TYPES = {
    BALANCE_CREDITED: 'balance_credited',
    BALANCE_DEBITED: 'balance_debited',
    RECHARGE_APPROVED: 'recharge_approved',
    RECHARGE_REJECTED: 'recharge_rejected'
  };

  static resolveBalanceTargetLabel(channel = '') {
    if (typeof channel === 'string' && channel.includes('credit')) {
      return 'credit';
    }

    return 'solde';
  }

  static resolveBalanceNotificationCopy(action, channel = '') {
    const target = this.resolveBalanceTargetLabel(channel);

    if (action === 'credited') {
      return target === 'credit'
        ? {
            title: 'Credit ajoute',
            messagePrefix: 'Votre credit a ete ajoute de'
          }
        : {
            title: 'Solde credite',
            messagePrefix: 'Votre solde a ete credite de'
          };
    }

    return target === 'credit'
      ? {
          title: 'Credit deduit',
          messagePrefix: 'Votre credit a ete deduit de'
        }
      : {
          title: 'Solde debite',
          messagePrefix: 'Votre solde a ete debite de'
        };
  }
  
  // Créer une nouvelle notification métier
  static async createNotification(userId, type, title, message, data = {}) {
    if (!userId || !type || !title || !message) {
      throw new Error('Paramètres manquants pour créer la notification');
    }
    
    try {
      const notificationRef = await addDoc(collection(db, 'notifications'), {
        userId,
        type,
        title,
        message,
        data,
        read: false,
        createdAt: serverTimestamp()
      });
      
      return {
        id: notificationRef.id,
        success: true
      };
    } catch (error) {
      console.error('Erreur lors de la création de la notification:', error);
      throw error;
    }
  }
  
  // Récupérer les notifications d'un utilisateur
  static async getUserNotifications(userId, limitCount = 20) {
    if (!userId) {
      throw new Error('ID utilisateur requis');
    }
    
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      const notifications = [];
      
      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date()
        });
      });
      
      return notifications;
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
      throw error;
    }
  }
  
  // Marquer une notification comme lue
  static async markAsRead(notificationId) {
    if (!notificationId) {
      throw new Error('ID de notification requis');
    }
    
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
      throw error;
    }
  }
  
  // Marquer toutes les notifications d'un utilisateur comme lues
  static async markAllAsRead(userId) {
    if (!userId) {
      throw new Error('ID utilisateur requis');
    }
    
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      const updatePromises = [];
      
      querySnapshot.forEach((doc) => {
        const notificationRef = doc.ref;
        updatePromises.push(
          updateDoc(notificationRef, {
            read: true,
            readAt: serverTimestamp()
          })
        );
      });
      
      await Promise.all(updatePromises);
      
      return { success: true, updatedCount: updatePromises.length };
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications:', error);
      throw error;
    }
  }
  
  // Compter les notifications non lues d'un utilisateur
  static async getUnreadCount(userId) {
    if (!userId) {
      throw new Error('ID utilisateur requis');
    }
    
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Erreur lors du comptage des notifications non lues:', error);
      throw error;
    }
  }
  
  // Notifications métier spécifiques
  
  // Notification de crédit de solde
  static async notifyBalanceCredited(userId, amount, source = 'recharge') {
    const copy = this.resolveBalanceNotificationCopy('credited', source);
    return await this.createNotification(
      userId,
      this.NOTIFICATION_TYPES.BALANCE_CREDITED,
      copy.title,
      `${copy.messagePrefix} ${amount.toLocaleString('fr-DZ')} DZD`,
      { amount, source, timestamp: new Date().toISOString() }
    );
  }
  
  // Notification de débit de solde
  static async notifyBalanceDebited(userId, amount, reason = 'purchase') {
    const copy = this.resolveBalanceNotificationCopy('debited', reason);
    return await this.createNotification(
      userId,
      this.NOTIFICATION_TYPES.BALANCE_DEBITED,
      copy.title,
      `${copy.messagePrefix} ${amount.toLocaleString('fr-DZ')} DZD`,
      { amount, reason, timestamp: new Date().toISOString() }
    );
  }
  

  
  // Notification de demande de recharge
  static async notifyRechargeRequest(userId, amount, method) {
    return await this.createNotification(
      userId,
      this.NOTIFICATION_TYPES.RECHARGE_REQUEST,
      'Demande de recharge envoyée',
      `Votre demande de recharge de ${amount.toLocaleString('fr-DZ')} DZD via ${method} a été envoyée`,
      { amount, method, status: 'pending' }
    );
  }
  
  // Notification d'approbation de recharge
  static async notifyRechargeApproved(userId, amount, method) {
    return await this.createNotification(
      userId,
      this.NOTIFICATION_TYPES.RECHARGE_APPROVED,
      'Demande de recharge approuvée',
      `Votre demande de recharge de ${amount.toLocaleString('fr-DZ')} DZD via ${method} a été approuvée`,
      { amount, method, status: 'approved' }
    );
  }
  
  // Notification de rejet de recharge
  static async notifyRechargeRejected(userId, amount, reason) {
    return await this.createNotification(
      userId,
      this.NOTIFICATION_TYPES.RECHARGE_REJECTED,
      'Demande de recharge rejetée',
      `Votre demande de recharge de ${amount.toLocaleString('fr-DZ')} DZD a été rejetée. Raison: ${reason}`,
      { amount, reason, status: 'rejected' }
    );
  }
  

}

export default NotificationService;
